import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import CheckIcon from "@mui/icons-material/Check"
import InboxIcon from "@mui/icons-material/Inbox"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import {
  getAvailableDCsSelector,
} from "../../../../reducers/Organizations/organizations"
import { attachDistributionCenters } from "../../../../actions/Organizations/Enterprise"

const makeEmptyParams = () => ({
  packagingNotes: "",
  deliveryScheduleNotes: "",
  minimumDeliveryVolume: "",
})

const validateParams = (p) => {
  const errs = {}
  if (p.packagingNotes.length > 1000) errs.packagingNotes = "1000 caractères max"
  if (p.deliveryScheduleNotes.length > 500) errs.deliveryScheduleNotes = "500 caractères max"
  if (
    p.minimumDeliveryVolume !== "" &&
    (!/^\d+$/.test(p.minimumDeliveryVolume) || Number(p.minimumDeliveryVolume) < 0)
  ) {
    errs.minimumDeliveryVolume = "Veuillez saisir un nombre entier positif"
  }
  return errs
}

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 720,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "12px",
      display: "flex",
      flexDirection: "column",
      maxHeight: "90vh",
    },
  },
  title: {
    px: "40px",
    pt: "32px",
    pb: 1,
    marginBottom: "30px",
    fontSize: 20,
    fontWeight: 400,
    pr: "76px",
  },
  closeBtn: {
    position: "absolute",
    right: "32px",
    top: "24px",
    width: 36,
    height: 36,
    p: 0,
    color: "#555555",
  },
  content: {
    px: 0,
    py: 0,
    pt: 3,
    display: "flex",
    flexDirection: "column",
    gap: 1.75,
    overflow: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
  },
  searchWrap: {
    mx: "40px",
    flexShrink: 0,
  },
  meta: {
    mx: "40px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  metaCount: {
    fontSize: 12,
    color: "text.secondary",
  },
  clearAll: {
    fontSize: 14,
    fontWeight: 500,
    color: "primary.main",
    background: "none",
    border: "none",
    cursor: "pointer",
    p: "4px 8px",
    borderRadius: 1,
    fontFamily: "inherit",
    "&:hover": { bgcolor: "primary.50" },
  },
  pickList: {
    mx: "40px",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1.5,
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
    bgcolor: "background.paper",
  },
  pickRow: {
    borderBottom: "1px solid",
    borderColor: "divider",
    transition: "background 120ms",
    "&:last-child": { borderBottom: "none" },
  },
  pickRowSelected: {
    bgcolor: "#F0F9FF",
  },
  pickRowHead: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
    px: 2,
    py: 2,
    cursor: "pointer",
    userSelect: "none",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 0.5,
    border: "1.5px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: "2px",
    transition: "all 150ms",
  },
  checkboxUnchecked: {
    borderColor: "grey.400",
    bgcolor: "background.paper",
  },
  checkboxChecked: {
    borderColor: "primary.main",
    bgcolor: "primary.main",
    color: "white",
  },
  pickId: {
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    flex: 1,
    minWidth: 0,
  },
  pickName: {
    fontSize: 16,
    fontWeight: 500,
    color: "text.primary",
  },
  pickCode: {
    fontSize: 12,
    color: "text.secondary",
    letterSpacing: "0.02em",
  },
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    mt: 1,
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 14,
    color: "text.secondary",
    p: 0,
    userSelect: "none",
  },
  paramsWrap: {
    pt: 1,
    pr: 3,
    pb: 3,
    pl: "48px",
  },
  paramsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  paramsVolumeWrap: {
    gridColumn: "1 / -1",
    maxWidth: 260,
  },
  paramsField: {
    display: "flex",
    flexDirection: "column",
  },
  paramsLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#757575",
    mb: 1,
  },
  paramsTextarea: {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#ffffff",
      fontSize: 14,
      "& fieldset": { borderColor: "#E0E0E0" },
      "&:hover fieldset": { borderColor: "#9E9E9E" },
    },
  },
  paramsVolumeField: {
    "& .MuiInput-root": { fontSize: 14 },
    "& .MuiInput-underline:before": { borderBottomColor: "#E0E0E0" },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#9E9E9E" },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    py: 5,
    gap: 1.5,
  },
  emptyIcon: {
    fontSize: 48,
    color: "text.disabled",
  },
  emptyText: {
    fontSize: 16,
    color: "text.secondary",
    maxWidth: 320,
  },
  serverError: {
    mx: "40px",
    mt: 1,
    fontSize: 14,
    color: "error.main",
    flexShrink: 0,
  },
  actions: {
    px: "40px",
    pt: 3,
    pb: 4,
    gap: 1,
    borderTop: "1px solid",
    borderColor: "divider",
    flexShrink: 0,
  },
  footerLeft: {
    flex: 1,
    fontSize: 14,
    color: "text.secondary",
  },
}

const AttachDCsModal = ({ open, onClose, enterpriseId, alreadyAttachedIds }) => {
  const dispatch = useDispatch()
  const availableDCs = useSelector(getAvailableDCsSelector)

  const [query, setQuery] = useState("")
  const [picked, setPicked] = useState({})
  const [expanded, setExpanded] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState(null)

  const available = availableDCs.filter(dc => !alreadyAttachedIds.includes(dc.objectId))
  const filtered = available.filter(dc => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return dc.name.toLowerCase().includes(q) || (dc.code || "").toLowerCase().includes(q)
  })

  const pickedIds = Object.keys(picked)

  const allErrors = {}
  pickedIds.forEach(id => {
    const errs = validateParams(picked[id])
    if (Object.keys(errs).length) allErrors[id] = errs
  })

  const hasParamErrors = Object.keys(allErrors).length > 0
  const noneSelected = pickedIds.length === 0

  const togglePick = (id) => {
    setPicked(prev => {
      const next = { ...prev }
      if (next[id]) {
        delete next[id]
      } else {
        next[id] = makeEmptyParams()
      }
      return next
    })
    setExpanded(prev => {
      if (prev[id]) {
        const next = { ...prev }
        delete next[id]
        return next
      }
      return prev
    })
  }

  const toggleExpanded = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const updateParam = (id, field, value) => {
    setPicked(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }))
  }

  const handleClose = () => {
    setPicked({})
    setExpanded({})
    setQuery("")
    setSubmitted(false)
    setServerError(null)
    onClose()
  }

  const handleSave = async () => {
    setSubmitted(true)
    if (noneSelected || hasParamErrors) return
    setSubmitting(true)
    setServerError(null)
    try {
      const newLinks = pickedIds.map(id => {
        const dc = availableDCs.find(d => d.objectId === id)
        const p = picked[id]
        return {
          dc: { __type: "Pointer", className: "DistributionCenters", objectId: id },
          dcName: dc.name,
          dcCode: dc.code || "",
          packagingNotes: p.packagingNotes,
          deliveryScheduleNotes: p.deliveryScheduleNotes,
          minimumDeliveryVolume: p.minimumDeliveryVolume === "" ? 0 : parseInt(p.minimumDeliveryVolume, 10),
        }
      })
      await dispatch(attachDistributionCenters(enterpriseId, newLinks))
      handleClose()
    } catch {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  if (available.length === 0 && open) {
    return (
      <Dialog open={open} onClose={handleClose} sx={sx.dialog}>
        <DialogTitle sx={sx.title}>
          Rattacher un site de livraison
          <IconButton onClick={handleClose} sx={sx.closeBtn}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...sx.content, px: 4, pt: 3 }}>
          <Box sx={sx.emptyState}>
            <InboxIcon sx={sx.emptyIcon} />
            <Typography sx={sx.emptyText}>
              Aucun site de livraison disponible. Contactez votre administrateur.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={sx.actions}>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={handleClose}>Fermer</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} sx={sx.dialog}>
      <DialogTitle sx={sx.title}>
        Rattacher un site de livraison
        <IconButton onClick={handleClose} disabled={isSubmitting} sx={sx.closeBtn}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={sx.content}>
        <Box sx={sx.searchWrap}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher un site par nom ou code"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery("")} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Box sx={sx.meta}>
          <Typography sx={sx.metaCount}>
            {filtered.length} site{filtered.length > 1 ? "s" : ""} disponible{filtered.length > 1 ? "s" : ""}
          </Typography>
          {pickedIds.length > 0 && (
            <Box
              component="button"
              sx={sx.clearAll}
              onClick={() => { setPicked({}); setExpanded({}) }}
            >
              Tout désélectionner
            </Box>
          )}
        </Box>

        <Box sx={sx.pickList}>
          {filtered.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center", fontSize: 14, color: "text.secondary" }}>
              Aucun résultat pour «&nbsp;{query}&nbsp;»
            </Typography>
          ) : (
            filtered.map(dc => {
              const isPicked = Boolean(picked[dc.objectId])
              const isExpanded = Boolean(expanded[dc.objectId])
              const fieldErrors = allErrors[dc.objectId] || {}
              const params = picked[dc.objectId] || makeEmptyParams()

              return (
                <Box
                  key={dc.objectId}
                  sx={{ ...sx.pickRow, ...(isPicked ? sx.pickRowSelected : {}) }}
                >
                  <Box
                    sx={sx.pickRowHead}
                    onClick={() => togglePick(dc.objectId)}
                  >
                    <Box sx={{ ...sx.checkbox, ...(isPicked ? sx.checkboxChecked : sx.checkboxUnchecked) }}>
                      {isPicked && <CheckIcon sx={{ fontSize: 14 }} />}
                    </Box>
                    <Box sx={sx.pickId}>
                      <Typography sx={sx.pickName}>{dc.name}</Typography>
                      {dc.code && <Typography sx={sx.pickCode}>{dc.code}</Typography>}
                      {isPicked && (
                        <Box
                          component="button"
                          sx={sx.toggleBtn}
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(dc.objectId) }}
                        >
                          <Box component="span" sx={{ fontWeight: 400 }}>
                            {isExpanded ? "Masquer les détails" : "Ajouter des détails"}
                          </Box>
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: 18,
                              transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {isPicked && (
                    <Collapse in={isExpanded} unmountOnExit>
                      <Box sx={sx.paramsWrap}>
                        <Box sx={sx.paramsGrid}>
                          <Box sx={sx.paramsField}>
                            <Typography sx={sx.paramsLabel}>Notes packaging</Typography>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              size="small"
                              // eslint-disable-next-line quotes
                              placeholder={`ex : "pas de fourreau", "cartons uniquement"`}
                              value={params.packagingNotes}
                              onChange={e => updateParam(dc.objectId, "packagingNotes", e.target.value)}
                              error={Boolean(fieldErrors.packagingNotes)}
                              helperText={fieldErrors.packagingNotes}
                              inputProps={{ maxLength: 1000 }}
                              sx={sx.paramsTextarea}
                              onClick={e => e.stopPropagation()}
                            />
                          </Box>
                          <Box sx={sx.paramsField}>
                            <Typography sx={sx.paramsLabel}>Créneaux de livraison</Typography>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              size="small"
                              // eslint-disable-next-line quotes
                              placeholder={`ex : "Frais 5h-7h / Épicerie 5h-11h, lundi-samedi"`}
                              value={params.deliveryScheduleNotes}
                              onChange={e => updateParam(dc.objectId, "deliveryScheduleNotes", e.target.value)}
                              error={Boolean(fieldErrors.deliveryScheduleNotes)}
                              helperText={fieldErrors.deliveryScheduleNotes}
                              inputProps={{ maxLength: 500 }}
                              sx={sx.paramsTextarea}
                              onClick={e => e.stopPropagation()}
                            />
                          </Box>
                          <Box sx={sx.paramsVolumeWrap}>
                            <Box sx={sx.paramsField}>
                              <Typography sx={sx.paramsLabel}>Volume min. par livraison</Typography>
                              <TextField
                                fullWidth
                                variant="standard"
                                placeholder="0"
                                value={params.minimumDeliveryVolume}
                                onChange={e => updateParam(dc.objectId, "minimumDeliveryVolume", e.target.value)}
                                error={Boolean(fieldErrors.minimumDeliveryVolume)}
                                helperText={fieldErrors.minimumDeliveryVolume}
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">/ plats</InputAdornment>,
                                }}
                                sx={sx.paramsVolumeField}
                                onClick={e => e.stopPropagation()}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              )
            })
          )}
        </Box>

        {submitted && noneSelected && (
          <FormHelperText error sx={{ mx: "40px", flexShrink: 0 }}>
            Veuillez sélectionner au moins un site de livraison.
          </FormHelperText>
        )}
      </DialogContent>

      {serverError && (
        <Typography sx={sx.serverError}>{serverError}</Typography>
      )}

      <DialogActions sx={sx.actions}>
        <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Typography sx={sx.footerLeft}>
          {pickedIds.length > 0
            ? `${pickedIds.length} site${pickedIds.length > 1 ? "s" : ""} sélectionné${pickedIds.length > 1 ? "s" : ""}`
            : ""}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSubmitting || (submitted && (noneSelected || hasParamErrors))}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting
            ? "Enregistrement…"
            : pickedIds.length > 0
              ? `Rattacher ${pickedIds.length} site${pickedIds.length > 1 ? "s" : ""}`
              : "Rattacher"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AttachDCsModal
